import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Accessibility.css';
import ScrollIndicator from '../ScrollIndicator';
import TearFaceIcon from '../TearFaceIcon';
import { useAnalytics } from '../../utils/analytics/useAnalytics';

const sections = [
  { id: 'accessibility-hero-section', label: 'Welcome' },
  { id: 'accessibility-facts-section', label: 'Why It Matters' },
  { id: 'accessibility-interactive-section', label: 'Our Impact' },
  { id: 'accessibility-advantages-section', label: 'Features' },
  { id: 'accessibility-plans-section', label: 'Pricing' },
  { id: 'accessibility-faq-section', label: 'FAQs' }
];

const facts = [
  { 
    image: 'accessibility-matters1.png', 
    alt: '1 in 4 adults lives with a disability.',
    link: 'https://www.cdc.gov/media/releases/2024/s0716-Adult-disability.html'
  },
  { 
    image: 'accessibility-matters2.png', 
    alt: 'Accessible sites rank higher in SEO.',
    link: 'https://www.accessibilitychecker.org/research-papers/accessibility-seo-impact-study/'
  },
  { 
    image: 'accessibility-matters3.png', 
    alt: 'Increase conversion rates by up to 35%.',
    link: 'https://www.netguru.com/blog/web-accessibility'
  },
  { 
    image: 'accessibility-matters4.png', 
    alt: 'Enhanced Brand Reputation and Trust',
    link: 'https://cloud.google.com/blog/topics/consumer-packaged-goods/data-shows-shoppers-prioritizing-sustainability-and-values'
  }
];

const advantages = [
  {
    title: 'One Button to rule them all',
    detail: 'Auto apply required accessibility features. - Beta Preview',
    image: 'accessibility-advantages1.png'
  },
  {
    title: '50+ Languages',
    detail: 'Supports all major locales.',
    image: 'accessibility-advantages2.png'
  },
  {
    title: 'Customizable colours',
    detail: 'Change widget colours to match your brand.',
    image: 'accessibility-advantages3.png'
  },
  {
    title: 'Continuous Updates',
    detail: 'We are constantly improving our widget.',
    image: 'accessibility-advantages4.png'
  }
];

const faqs = [
  {
    question: "How does the accessibility widget work?",
    answer: "The widget lets people personalize how they view a website—adjusting text size, colors, contrast, navigation, and more. Settings are saved per website, so when you return to the same site your preferences are remembered until you reset them."
  },
  {
    question: "Can I try the widget before using it for my business?",
    answer: "Yes! Individual users can install our free browser extension for Chrome or Firefox and try all the accessibility features on any website—including your own. This way you can see exactly how it works before choosing the business plan."
  },
  {
    question: "How do businesses integrate the widget?",
    answer: ["Integration is simple—just add one line of code to your website. ",
            <Link key="contact" to="/contact-us" className="faq-link">Contact us</Link>,
            " to get your unique code and setup instructions."]
  },
  {
    question: "How do individual users get started?",
    answer: "Simply install our free browser extension from the Chrome Web Store or Firefox Add-ons. Once installed, you can enable accessibility tools on any website you visit."
  },
  {
    question: "What does 'pay only when it delivers value' mean?",
    answer: "We believe accessibility should empower, not burden. That’s why businesses only pay when the widget is making a real difference—helping your users and supporting your goals. If it doesn’t bring value, we don’t want cost to be the reason you hold back on accessibility."
  },
  {
    question: "Can I customize the widget's appearance and language?",
    answer: "Yes. Businesses can match the widget’s colors and default language to their brand. Individual users can choose their preferred colors and language on every site they visit."
  },
  {
    question: "Is the widget compatible with most websites?",
    answer: "Yes. The widget is designed to work on nearly all websites without interfering with existing features. While it’s impossible to test every site, it has passed review by Chrome and Firefox stores to ensure wide compatibility."
  },
  {
    question: "Will the widget affect my website's performance?",
    answer: "No. It loads in the background, so it won’t slow down your site. If the widget ever fails to load, your website will continue working normally. It runs independently of your site’s code."
  },
  {
    question: "Is the widget secure and privacy-focused?",
    answer: "Yes. The widget is lightweight (~400KB) and privacy-friendly. User preferences are stored locally—no personal data is sent to outside servers. It’s also reviewed and approved by Chrome and Firefox stores for security."
  },
  {
    question: "Does the widget work on mobile devices?",
    answer: "Yes. The widget is fully responsive and works on mobile, tablet, and desktop. Because devices vary, we recommend testing it with the ones most used by your audience."
  },
  {
    question: "Does the widget ensure accessibility compliance?",
    answer: "The widget improves usability but doesn’t guarantee legal compliance. Businesses remain responsible for meeting accessibility standards and may need professional audits for full compliance."
  }
];

const Accessibility = () => {
  const { track, trackButton } = useAnalytics();
  const [selectedPlan, setSelectedPlan] = useState('single');
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isWidgetActive, setIsWidgetActive] = useState(false);
  const [activeFaq, setActiveFaq] = useState(null);
  const previewRef = useRef(null);

  const resetWidgetPreferences = () => {
    // Remove widget-related localStorage/sessionStorage keys
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('accessibility-widget') || key.startsWith('AccessibilityWidget')) {
        localStorage.removeItem(key);
      }
    });
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith('accessibility-widget') || key.startsWith('AccessibilityWidget')) {
        sessionStorage.removeItem(key);
      }
    });
    // Remove widget cookies
    if (document.cookie) {
      document.cookie.split(';').forEach(cookie => {
        const eqPos = cookie.indexOf('=');
        const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
        if (name.startsWith('accessibility-widget') || name.startsWith('AccessibilityWidget')) {
          document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
        }
      });
    }
  };

  // Widget Click Animation Handlers
  const handleWidgetActivation = () => {
    const previewElement = previewRef.current;
    
    if (!previewElement) {
      setIsWidgetActive(true);
      return;
    }

    // Add the fly-to-bottom-right animation
    previewElement.classList.add('fly-out');
    
    // Optional: Add destination pulse effect
    setTimeout(() => {
      const pulse = document.createElement('div');
      pulse.className = 'widget-destination-pulse';
      document.body.appendChild(pulse);
      
      // Remove pulse after animation
      setTimeout(() => {
        if (document.body.contains(pulse)) {
          document.body.removeChild(pulse);
        }
      }, 300);
    }, 500); // Pulse appears when preview reaches bottom-right
    
    // Activate widget after preview reaches destination
    setTimeout(() => {
      setIsWidgetActive(true);
    }, 800); // Match the CSS animation duration
  };

  // Alternative: More dramatic "sucked in" effect
  const handleSuckedInActivation = () => {
    const previewElement = previewRef.current;
    
    if (!previewElement) {
      setIsWidgetActive(true);
      return;
    }

    // Use the "sucked-in" animation instead
    previewElement.classList.add('sucked-in');
    
    // Create a trail effect
    const trail = document.createElement('div');
    trail.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      width: 2px;
      height: 2px;
      background: linear-gradient(45deg, rgba(45, 112, 229, 0.8), rgba(45, 112, 229, 0.2));
      border-radius: 50%;
      transform: translate(-50%, -50%);
      animation: trailToBottomRight 0.7s ease-out forwards;
      pointer-events: none;
      z-index: 999;
    `;
    
    document.body.appendChild(trail);
    
    // Remove trail and activate widget
    setTimeout(() => {
      if (document.body.contains(trail)) {
        document.body.removeChild(trail);
      }
      setIsWidgetActive(true);
    }, 700);
  };

  // Add trail animation CSS (add this to your CSS file)
  const trailCSS = `
  @keyframes trailToBottomRight {
    0% {
      width: 2px;
      height: 2px;
      opacity: 1;
    }
    50% {
      width: 100px;
      height: 4px;
      opacity: 0.8;
      transform: translate(-50%, -50%) rotate(45deg);
    }
    100% {
      width: 200px;
      height: 2px;
      opacity: 0;
      transform: translate(35vw, 30vh) rotate(45deg);
    }
  }
  `;

  // Enhanced version with calculated trajectory
  const handleCalculatedTrajectory = () => {
    const previewElement = previewRef.current;
    
    if (!previewElement) {
      setIsWidgetActive(true);
      return;
    }

    // Get preview element position
    const previewRect = previewElement.getBoundingClientRect();
    const previewCenterX = previewRect.left + previewRect.width / 2;
    const previewCenterY = previewRect.top + previewRect.height / 2;
    
    // Calculate distance to bottom-right corner
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    const targetX = windowWidth - 100; // Widget position
    const targetY = windowHeight - 100;
    
    const deltaX = targetX - previewCenterX;
    const deltaY = targetY - previewCenterY;
    
    // Create custom CSS animation based on calculated trajectory
    const customAnimation = `
      @keyframes customFlyToWidget {
        0% {
          transform: translateY(0) scale(1) rotate(0deg);
          opacity: 1;
        }
        100% {
          transform: translate(${deltaX}px, ${deltaY}px) scale(0.1) rotate(30deg);
          opacity: 0;
          visibility: hidden;
        }
      }
    `;
    
    // Inject custom animation
    const style = document.createElement('style');
    style.textContent = customAnimation;
    document.head.appendChild(style);
    
    // Apply animation
    previewElement.style.animation = 'customFlyToWidget 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards';
    
    setTimeout(() => {
      setIsWidgetActive(true);
      // Clean up
      document.head.removeChild(style);
    }, 800);
  };

  // Load accessibility widget only when active
  useEffect(() => {
    if (!isWidgetActive) {
      return;
    }
    const cleanup = () => {
      const existingWidgetContainer = document.querySelector('.accessibilityWidgetContainer');
      if (existingWidgetContainer) {
        existingWidgetContainer.remove();
      }
      const existingStyles = document.querySelectorAll('style[data-widget-styles]');
      existingStyles.forEach(style => style.remove());
      window.AccessibilityWidget = null;
      resetWidgetPreferences();
    };
    const loadWidget = () => {
      cleanup();
      const script = document.createElement('script');
      script.id = 'tf-accessibility-widget';
      script.src = '/widgets/accessibility/accessibility-widget.min.js';
      script.setAttribute('data-api-key', 'tearface-website');
      script.setAttribute('data-size', 'M');
      script.setAttribute('data-corner', 'bottomRight');
      script.setAttribute('data-theme', 'light');
      script.setAttribute('data-color', '#007bff');
      script.onload = () => {
        const checkWidget = setInterval(() => {
          if (window.AccessibilityWidget && window.AccessibilityWidget.instance) {
            clearInterval(checkWidget);
          }
        }, 100);
      };
      document.body.appendChild(script);
    };
    loadWidget();
    return () => {
      cleanup();
    };
  }, [isWidgetActive]);

  // Always cleanup widget on unmount or navigation
  useEffect(() => {
    return () => {
      const existingWidgetContainer = document.querySelector('.accessibilityWidgetContainer');
      if (existingWidgetContainer) {
        existingWidgetContainer.remove();
      }
      const existingStyles = document.querySelectorAll('style[data-widget-styles]');
      existingStyles.forEach(style => style.remove());
      window.AccessibilityWidget = null;
      resetWidgetPreferences();
    };
  }, []);

  // Track page view
  useEffect(() => {
    track('page_view', {
      category: 'Accessibility',
      label: 'Accessibility Page'
    });
  }, [track]);

  // Detect mobile
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 1024);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Scroll handler for image transitions
  useEffect(() => {
    let lastScrollY = window.scrollY;
    let frameId = null;

    const handleScroll = () => {
      if (frameId) {
        return;
      }

      frameId = requestAnimationFrame(() => {
        const scrollY = window.scrollY;
        const vh = window.innerHeight;
        const blocks = document.querySelectorAll('.accessibility-content-block');
        
        blocks.forEach((block, index) => {
          const rect = block.getBoundingClientRect();
          const isVisible = rect.top < vh * 0.75 && rect.bottom > vh * 0.25;
          
          if (isVisible) {
            block.classList.add('visible');
            setActiveImageIndex(index);
          } else {
            block.classList.remove('visible');
          }
        });

        // Keep the container active while in the interactive section
        const section = document.querySelector('.accessibility-interactive-section');
        if (section) {
          const container = document.querySelector('.accessibility-effect-card-container');
          const sectionRect = section.getBoundingClientRect();
          
          if (sectionRect.top < vh && sectionRect.bottom > 0) {
            container?.classList.add('active');
          } else {
            container?.classList.remove('active');
          }
        }

        lastScrollY = scrollY;
        frameId = null;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      if (frameId) {
        cancelAnimationFrame(frameId);
      }
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handlePlanToggle = (plan) => {
    setSelectedPlan(plan);
    track('pricing_toggle', {
      category: 'Pricing',
      label: plan === 'single' ? 'Single User' : 'Business',
      value: plan === 'single' ? 0 : 25
    });
  };

  const toggleFaq = (i) => {
    setActiveFaq(activeFaq === i ? null : i);
  };

  return (
    <>
      <ScrollIndicator sections={sections} />
      <TearFaceIcon />
      
      <div className='accessibility-page'>
        <div className='accessibility-section accessibility-hero-section' id='accessibility-hero-section'>
          <div className='accessibility-hero-content'>
            <div className='accessibility-hero-text'>
              <h1>
                Accessibility<span className='comma'>,</span><br/>
                For Everyone<span className='dot'>.</span>
              </h1>
              <p>
                Transform your website into an inclusive space where every visitor feels welcome. 
                Join thousands of businesses already making a difference with our powerful accessibility tools.
              </p>
              <div className="accessibility-hero-buttons">
                <a 
                  href="#accessibility-plans-section" 
                  className="accessibility-hero-button accessibility-hero-button-primary"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById('accessibility-plans-section').scrollIntoView({ behavior: 'smooth' });
                  }}
                >
                  Get Started
                </a>
                <a href="/contact-us" className="accessibility-hero-button accessibility-hero-button-secondary">
                  Contact Us
                </a>
              </div>
            </div>
          {!isWidgetActive && (
            <div
              className='accessibility-widget-preview'
              ref={previewRef}
              tabIndex={0}
              role='button'
              aria-label='Open accessibility widget preview'
              style={{cursor: 'pointer'}}
              onClick={handleWidgetActivation} // or handleSuckedInActivation or handleCalculatedTrajectory
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleWidgetActivation();
                }
              }}
            />
          )}
          </div>
        </div>

        <div className='accessibility-section accessibility-facts-section' id='accessibility-facts-section'>
          <h2>Why Accessibility Matters</h2>
          <div className='accessibility-facts-grid'>
            {facts.map((fact, index) => (
              <a 
                key={index} 
                href={fact.link}
                target="_blank"
                rel="noopener noreferrer"
                className='accessibility-fact-card'
              >
                <div className="fact-card-content">
                  <img src={`/images/${fact.image}`} alt={fact.alt} />
                  <div className="fact-card-overlay">
                    <span className="read-more">Read More →</span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>

        <div className='accessibility-section accessibility-interactive-section' id='accessibility-interactive-section'>
          <h2>Making Websites<span className='comma'>,</span><br /> More Inclusive<span className='dot'>.</span></h2>
          
          <div className='accessibility-interactive-content'>
            <div className='accessibility-content-block-wrapper'>
              <div className='accessibility-content-block'>
                <img 
                  src='/images/accessibility-facts-buttons1.png'
                  alt='Text size and line height adjustment buttons'
                  className='accessibility-feature-icon'
                />
                <p>
                  Enhance <span>readability</span> with our intuitive text controls. Users can easily 
                  <span>adjust text size</span> and <span>line spacing</span> with just two clicks, making 
                  content more comfortable to read for everyone, especially those with visual impairments.
                </p>
              </div>
              <div className='accessibility-content-block'>
                <img 
                  src='/images/accessibility-facts-buttons2.png'
                  alt='Structured navigation menu button'
                  className='accessibility-feature-icon'
                />
                <p>
                  Transform any website with our <span>smart navigation system</span>. One click creates an 
                  <span>organized menu structure</span>, helping users with screen readers and cognitive 
                  disabilities to <span>navigate effortlessly</span> through your content.
                </p>
              </div>
              <div className='accessibility-content-block'>
                <img 
                  src='/images/accessibility-facts-buttons3.png'
                  alt='Grayscale mode toggle button'
                  className='accessibility-feature-icon'
                />
                <p>
                  Our <span>grayscale mode</span> ensures content clarity for colorblind users and those with 
                  color vision deficiencies. Transform vibrant designs into <span>clear, readable</span> layouts 
                  while maintaining visual hierarchy and <span>information accessibility</span>.
                </p>
              </div>
              <div className='accessibility-content-block'>
                <img 
                  src='/images/accessibility-facts-buttons4.png'
                  alt='Animation and sound control buttons'
                  className='accessibility-feature-icon'
                />
                <p>
                  Create a <span>calm browsing experience</span> with our motion and audio controls. Users 
                  with vestibular disorders or sensory sensitivities can easily <span>pause animations</span> and 
                  <span>mute sounds</span>, making your website more comfortable to navigate.
                </p>
              </div>
            </div>
            {!isWidgetActive && (
              <div className='accessibility-effect-card-container'>
                <div className={`accessibility-effect-image-inner effect-image-1 ${activeImageIndex === 0 ? 'active' : ''}`}></div>
                <div className={`accessibility-effect-image-inner effect-image-2 ${activeImageIndex === 1 ? 'active' : ''}`}></div>
                <div className={`accessibility-effect-image-inner effect-image-3 ${activeImageIndex === 2 ? 'active' : ''}`}></div>
                <div className={`accessibility-effect-image-inner effect-image-4 ${activeImageIndex === 3 ? 'active' : ''}`}></div>
              </div>
            )}
          </div>
        </div>

        <div className='accessibility-section accessibility-advantages-section' id='accessibility-advantages-section'>
          <h2>Our Widget Advantages</h2>
          
          <div className='accessibility-advantages-grid'>
            {advantages.map((advantage, index) => (
              <div key={index} className='accessibility-advantage-card'>
                <div className='advantage-image'>
                  <img src={`/images/${advantage.image}`} alt={advantage.title} />
                </div>
                <h3>{advantage.title}</h3>
                <p>{advantage.detail}</p>
              </div>
            ))}
          </div>
        </div>

        <div className='accessibility-section accessibility-plans-section' id='accessibility-plans-section'>
          <h2>Empowering Accessibility<span className='dot'>.</span></h2>
          
          <div className='accessibility-plan-toggle'>
            <div className='accessibility-toggle-switch'>
              <button
                className={`accessibility-toggle-button ${selectedPlan === 'single' ? 'active' : ''}`}
                onClick={() => handlePlanToggle('single')}
              >
                Single User
              </button>
              <button
                className={`accessibility-toggle-button ${selectedPlan === 'business' ? 'active' : ''}`}
                onClick={() => handlePlanToggle('business')}
              >
                Business
              </button>
              <div className='accessibility-toggle-slider'></div>
            </div>
          </div>

          <div className='accessibility-plan-description'>
            {selectedPlan === 'single' ? (
              <>
                <h3>Free to Use</h3>
                <p>Install our browser extension for <a href="https://chromewebstore.google.com/detail/accessibility-widget/cdfmbkmodbplngfkbampdnkjblmjmmol" target="_blank" rel="noopener noreferrer" className="browser-link">Chrome</a> and <a href="https://addons.mozilla.org/en-US/firefox/addon/accessibility-widget/" target="_blank" rel="noopener noreferrer" className="browser-link">Firefox</a> to make any website accessible.</p>
                <p>Perfect for <span className='code-emphasis'>individual users</span> who want to browse the web with ease.</p>
              </>
            ) : (
              <>
                <h3>$10/Month</h3>
                <p>Integrate with <span className='code-emphasis'>just one line of code</span>. No contracts, pay only when it delivers value.</p>
                <p>Get <span className='code-emphasis'>priority support</span>, <span className='code-emphasis'>monthly insights</span>, <span className='code-emphasis'>99.9% uptime</span>, and <span className='code-emphasis'>early access</span> to new features.</p>
              </>
            )}
          </div>
        </div>

        <div className='accessibility-section accessibility-faq-section' id='accessibility-faq-section'>
          <h2>Questions<span className='comma'>,</span> Answers<span className='dot'>.</span></h2>
          
          <div className='accessibility-faq-container'>
            {faqs.map((faq, i) => (
              <div
                key={i}
                className={`accessibility-faq-card ${activeFaq === i ? 'active' : ''}`}
                onClick={() => toggleFaq(i)}
              >
                <div className="accessibility-faq-question">{faq.question}</div>
                <div className="accessibility-faq-answer">{faq.answer}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default Accessibility;
